/**
 * Publish Preview store
 * ---------------------
 * Single source of truth for the async Publish Preview UX. Owns:
 *   - active simulationId + status
 *   - latest validity check (currentDraftHash vs job.draftHash)
 *   - poll loop while a job is processing
 *   - reset hooks for ConfigDraft saves (stale-on-edit)
 *
 * Why a store and not view-local state?
 *   - The "Publish Changes" button on UnpublishedChanges.vue must know if a
 *     valid preview exists to decide whether to show the warning dialog.
 *   - Navigation between PublishPreview.vue and UnpublishedChanges.vue must
 *     not cancel an in-flight job; the store keeps the polling alive.
 *
 * The store deliberately avoids strong coupling to any view. Views call
 * `runPreview()`, watch `previewStatus`, and call `requirePreviewBeforePublish()`
 * on the publish click path.
 */

import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import {
  getLatestPublishPreview,
  getPublishPreviewStatus,
  startPublishPreview,
  type PublishPreviewLatestResponse,
  type PublishPreviewOptions,
  type PublishPreviewProgress,
  type PublishPreviewResult,
  type PublishPreviewStatusResponse,
  type PublishPreviewStaleReason,
} from '@/services/publishPreviewService'

export type PreviewStatus =
  | 'idle' // never run for this session OR a draft save invalidated the previous run
  | 'processing' // job is in flight
  | 'previewed' // latest result matches current draftHash
  | 'stale' // result completed but drafts moved on (or hash mismatch)
  | 'failed' // worker failed
  | 'not_previewed' // alias used at the publish gate; same semantics as idle/stale/failed

const POLL_INTERVAL_MS = 2500

export const usePublishPreviewStore = defineStore('publishPreview', () => {
  /** True when a Run Preview HTTP call is mid-flight. */
  const startingPreview = ref(false)
  /** True when we are actively polling status. */
  const polling = ref(false)
  /** The active simulationId we are polling, if any. */
  const activeSimulationId = ref<string | null>(null)
  /** The draftHash captured when the active job started. */
  const activeDraftHash = ref<string | null>(null)
  /** Latest progress payload from the worker. */
  const progress = ref<PublishPreviewProgress | null>(null)
  /** Latest terminal-or-processing status payload. */
  const status = ref<PreviewStatus>('idle')
  /** Last completed result (full payload). */
  const result = ref<PublishPreviewResult | null>(null)
  /** Last terminal payload (drives banner messaging for stale/failed). */
  const lastTerminal = ref<PublishPreviewStatusResponse | null>(null)
  /** Latest /publish-preview/latest snapshot (drives the publish gate). */
  const latest = ref<PublishPreviewLatestResponse | null>(null)
  /** Last error message surfaced from the API. */
  const errorMessage = ref<string>('')
  /** Why the latest terminal preview is no longer fresh enough to show. */
  const staleReasons = ref<PublishPreviewStaleReason[]>([])

  let pollTimer: ReturnType<typeof setTimeout> | null = null

  // The store is "valid" when we have a complete preview whose draftHash
  // still matches the dealer's current draftHash. We use the /latest snapshot
  // as the source of truth for draft drift so other tabs / saves are picked
  // up even without an explicit reset call.
  const isPreviewValid = computed(() => {
    if (status.value !== 'previewed') return false
    if (latest.value && latest.value.isPreviewValid !== true) return false
    return true
  })

  function setError(message: string) {
    errorMessage.value = message
  }

  function clearError() {
    errorMessage.value = ''
  }

  function stopPolling() {
    if (pollTimer) {
      clearTimeout(pollTimer)
      pollTimer = null
    }
    polling.value = false
  }

  function applyTerminal(payload: PublishPreviewStatusResponse) {
    lastTerminal.value = payload
    if (payload.status === 'complete') {
      result.value = payload.result || null
      status.value = 'previewed'
      staleReasons.value = []
    } else if (payload.status === 'stale') {
      result.value = null
      status.value = 'stale'
      staleReasons.value = payload.staleReasons || ['config']
    } else if (payload.status === 'failed') {
      result.value = null
      status.value = 'failed'
      staleReasons.value = []
      setError(payload.errorMessage || 'Publish Preview failed.')
    }
  }

  /**
   * Single poll tick. Schedules the next tick if still processing.
   */
  async function pollOnce(params: { endpoint: string; sessionToken: string }) {
    const simulationId = activeSimulationId.value
    if (!simulationId) {
      stopPolling()
      return
    }

    let payload: PublishPreviewStatusResponse
    try {
      payload = await getPublishPreviewStatus({
        endpoint: params.endpoint,
        simulationId,
        sessionToken: params.sessionToken,
      })
    } catch (error) {
      // Transient failures should not abort polling immediately — we keep
      // trying for a few cycles. The user can also click Run Preview again.
      console.warn('publishPreview.pollFailed', error)
      pollTimer = setTimeout(() => pollOnce(params), POLL_INTERVAL_MS * 2)
      return
    }

    if (payload.status === 'processing') {
      progress.value = payload.progress || null
      status.value = 'processing'
      pollTimer = setTimeout(() => pollOnce(params), POLL_INTERVAL_MS)
      return
    }

    // Terminal state — stop polling and let the publish-gate logic re-resolve
    // validity using the /latest snapshot.
    stopPolling()
    activeSimulationId.value = null
    progress.value = null
    applyTerminal(payload)

    // Refresh the latest validity snapshot so the publish gate is up to date.
    void refreshLatest({
      endpoint: params.endpoint,
      jazelId: payload.jazelId,
      sessionToken: params.sessionToken,
    })
  }

  /**
   * Start a new Publish Preview run. Idempotent on the server side: if a
   * processing job already exists for the same draftHash the API returns its
   * simulationId (with `reused: true`) and we attach to it.
   */
  async function runPreview(params: {
    endpoint: string
    jazelId: string
    sessionToken: string
    options?: PublishPreviewOptions
  }): Promise<{ simulationId: string; reused: boolean } | null> {
    if (startingPreview.value) return null
    startingPreview.value = true
    clearError()
    try {
      const startPayload = await startPublishPreview({
        endpoint: params.endpoint,
        jazelId: params.jazelId,
        sessionToken: params.sessionToken,
        options: params.options,
      })

      activeSimulationId.value = startPayload.simulationId
      activeDraftHash.value = startPayload.draftHash
      progress.value = null
      status.value = 'processing'
      result.value = null
      lastTerminal.value = null
      staleReasons.value = []

      // Begin polling immediately. We don't await — the caller does not
      // block while the worker runs.
      stopPolling()
      polling.value = true
      pollTimer = setTimeout(
        () => pollOnce({ endpoint: params.endpoint, sessionToken: params.sessionToken }),
        POLL_INTERVAL_MS,
      )

      return {
        simulationId: startPayload.simulationId,
        reused: !!startPayload.reused,
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Failed to start Publish Preview.'
      setError(message)
      status.value = 'failed'
      return null
    } finally {
      startingPreview.value = false
    }
  }

  /**
   * Refresh the validity snapshot. Safe to call any time — eg. on view
   * mount, after a draft save, or when the user is about to click Publish.
   */
  async function refreshLatest(params: {
    endpoint: string
    jazelId: string
    sessionToken: string
  }): Promise<PublishPreviewLatestResponse | null> {
    try {
      const payload = await getLatestPublishPreview(params)
      latest.value = payload

      // Reconcile in-store status with server truth. A new draft save on
      // another tab will surface here as `isPreviewValid: false` and we
      // demote the local status accordingly.
      if (payload.latestStatus === 'processing') {
        // Adopt the in-flight job if we don't already have one.
        if (!activeSimulationId.value && payload.latestSimulationId) {
          activeSimulationId.value = payload.latestSimulationId
          activeDraftHash.value = payload.latestDraftHash
          status.value = 'processing'
          stopPolling()
          polling.value = true
          pollTimer = setTimeout(
            () => pollOnce({ endpoint: params.endpoint, sessionToken: params.sessionToken }),
            POLL_INTERVAL_MS,
          )
        }
      } else if (payload.latestStatus === 'complete' && payload.isPreviewValid) {
        // Treat as validated for current drafts. We don't auto-pull the
        // result payload here; views may explicitly fetch via simulationId.
        if (status.value !== 'previewed') {
          status.value = 'previewed'
        }
        staleReasons.value = []
      } else if (payload.latestStatus === 'complete' && !payload.isPreviewValid) {
        // Server says the latest complete job is no longer valid for current
        // drafts (drift). Demote to stale.
        status.value = 'stale'
        result.value = null
        staleReasons.value = payload.staleReasons || ['config']
      } else if (payload.latestStatus === 'failed') {
        status.value = 'failed'
        staleReasons.value = []
      } else if (payload.latestStatus === 'stale') {
        status.value = 'stale'
        result.value = null
        staleReasons.value = payload.staleReasons || ['config']
      }

      return payload
    } catch (error) {
      console.warn('publishPreview.refreshLatestFailed', error)
      return null
    }
  }

  /**
   * Hook for ConfigDraft save operations. Demotes the local status to
   * 'idle' so the publish gate will warn the user; we deliberately do NOT
   * cancel an in-flight job (the worker will detect the drift itself and
   * mark the result 'stale').
   */
  function notifyDraftChanged() {
    if (status.value === 'previewed' || status.value === 'stale' || status.value === 'failed') {
      status.value = 'idle'
      result.value = null
      staleReasons.value = ['config']
    }
    if (latest.value) {
      latest.value = { ...latest.value, isPreviewValid: false }
    }
  }

  /**
   * Convenience accessor for the publish gate. Returns true when there is a
   * fresh, valid, complete preview for the current draftHash. The dialog in
   * UnpublishedChanges.vue uses this to decide whether to warn the user.
   */
  function requirePreviewBeforePublish(): boolean {
    return isPreviewValid.value
  }

  /**
   * Reset the store (used on account switch / logout).
   */
  function reset() {
    stopPolling()
    activeSimulationId.value = null
    activeDraftHash.value = null
    progress.value = null
    status.value = 'idle'
    result.value = null
    lastTerminal.value = null
    latest.value = null
    errorMessage.value = ''
    staleReasons.value = []
  }

  return {
    // state
    startingPreview,
    polling,
    activeSimulationId,
    activeDraftHash,
    progress,
    status,
    result,
    lastTerminal,
    latest,
    errorMessage,
    staleReasons,
    isPreviewValid,

    // actions
    runPreview,
    refreshLatest,
    notifyDraftChanged,
    requirePreviewBeforePublish,
    reset,
  }
})
